import { Archive, ArrowCounterClockwise, DotsThree, PencilSimple, Trash } from '@phosphor-icons/react';
import { MenueEintrag, Popover } from '../../../../components/immobilien/ui';
import type { ObjektZeile } from '../../../../lib/immobilien/portfolio';
import type { ZustaendigOption } from '../../../../lib/immobilien/useObjektListe';
import { qm } from '../../../../lib/immobilien/format';
import { BUCHHALTUNG_FOLGT } from '../../../../lib/immobilien/hinweise';
import { fotoHintergrund } from '../../../../lib/immobilien/url';
import { ZustaendigMenue } from './ZustaendigMenue';

const SPALTEN = 'minmax(0,2.4fr) minmax(0,0.8fr) minmax(0,1fr) minmax(0,1.1fr) minmax(0,1fr) 36px';

/** Listenansicht (Prototyp): Objekt · Einheiten · Belegung · Zuständig · Saldo, plus Aktionen. */
export function ObjektTabelle({ zeilen, mitarbeiter, teams, aendertId, onOeffnen, onZustaendig, onBearbeiten, onLoeschen, onArchivieren }: {
  zeilen: ObjektZeile[];
  mitarbeiter: ZustaendigOption[];
  teams: ZustaendigOption[];
  aendertId: string | null;
  onOeffnen: (z: ObjektZeile) => void;
  onZustaendig: (z: ObjektZeile, ziel: ZustaendigOption | null) => void;
  onBearbeiten: (z: ObjektZeile) => void;
  onLoeschen: (z: ObjektZeile) => void;
  onArchivieren: (z: ObjektZeile, an: boolean) => void;
}) {
  const einheiten = zeilen.reduce((s, z) => s + z.einheiten, 0);
  const flaeche = zeilen.reduce((s, z) => s + z.flaeche, 0);
  return (
    <section className="immo-glas overflow-x-auto" style={{ borderRadius: 26 }} aria-label="Objekte als Liste">
      <div style={{ minWidth: 760 }}>
        <div role="row" className="grid items-center"
          style={{ gridTemplateColumns: SPALTEN, gap: 12, padding: '14px 20px', fontSize: 11, fontWeight: 600, color: 'var(--ink3)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--line)' }}>
          <span>Objekt</span><span>Einheiten</span><span>Belegung</span><span>Zuständig</span><span style={{ textAlign: 'right' }}>Saldo</span><span />
        </div>
        {zeilen.map(z => (
          <div key={z.id} role="row" className="immo-hover-flaeche grid items-center cursor-pointer"
            onClick={() => onOeffnen(z)}
            style={{ gridTemplateColumns: SPALTEN, gap: 12, padding: '12px 20px', borderBottom: '1px solid var(--line)', fontSize: 13 }}>
            <div className="flex items-center min-w-0" style={{ gap: 12 }}>
              <div className="shrink-0" style={{
                width: 44, height: 44, borderRadius: 12,
                background: fotoHintergrund(z.bildUrl) ?? 'repeating-linear-gradient(135deg,var(--fill2) 0 6px,var(--fill3) 6px 12px)',
              }} />
              <div className="flex flex-col min-w-0">
                <button type="button" onClick={e => { e.stopPropagation(); onOeffnen(z); }}
                  className="truncate text-left cursor-pointer" style={{ fontWeight: 600, border: 'none', background: 'transparent', padding: 0, color: 'var(--ink)' }}>
                  {z.nummer && <span className="immo-zahl" style={{ color: 'var(--ink3)', marginRight: 6 }}>{z.nummer}</span>}
                  {z.name}
                </button>
                <span className="truncate" style={{ fontSize: 12, color: 'var(--ink2)' }}>{z.adresse || 'Keine Adresse hinterlegt'}</span>
              </div>
            </div>
            <span className="immo-zahl">{z.einheiten}</span>
            <div className="flex items-center" style={{ gap: 8 }}>
              <div className="flex-1 overflow-hidden" style={{ height: 6, borderRadius: 3, background: 'var(--fill2)' }}>
                <div style={{ height: '100%', borderRadius: 3, background: 'var(--acc)', width: `${z.belegungProzent}%` }} />
              </div>
              <span className="immo-zahl" style={{ fontSize: 12, color: 'var(--ink2)', width: 36, textAlign: 'right' }}
                data-tip={z.mietEinheiten > 0 ? undefined : 'Keine Einheit in Vermietung durch uns (WEG ohne SEV) – kein Leerstand'}>{z.mietEinheiten > 0 ? `${z.belegungProzent}%` : '–'}</span>
            </div>
            <div className="min-w-0" onClick={e => e.stopPropagation()}>
              <ZustaendigMenue aktuell={z.zustaendig} aktuellId={z.zustaendigId} mitarbeiter={mitarbeiter} teams={teams}
                laeuft={aendertId === z.id} onWahl={ziel => onZustaendig(z, ziel)} />
            </div>
            <span className="immo-zahl" title={`Saldo: ${BUCHHALTUNG_FOLGT}`} style={{ textAlign: 'right', fontWeight: 700, color: 'var(--ink3)' }}>–</span>
            <div onClick={e => e.stopPropagation()}>
              <Popover
                label={`Aktionen für ${z.name}`}
                breite={180}
                ausloeser={({ offen, umschalten, ref }) => (
                  <button ref={ref} type="button" aria-label={`Aktionen für ${z.name}`} aria-haspopup="menu" aria-expanded={offen} onClick={umschalten}
                    className="immo-hover-flaeche flex items-center justify-center cursor-pointer"
                    style={{ width: 32, height: 32, borderRadius: 10, border: 'none', background: 'transparent', color: 'var(--ink2)' }}>
                    <DotsThree weight="bold" size={18} />
                  </button>
                )}
              >
                {schliessen => (
                  <>
                    <MenueEintrag onClick={() => { schliessen(); onBearbeiten(z); }}><PencilSimple size={15} />Bearbeiten</MenueEintrag>
                    <MenueEintrag onClick={() => { schliessen(); onArchivieren(z, !z.archiviert); }}>
                      {z.archiviert ? <><ArrowCounterClockwise size={15} />Wiederherstellen</> : <><Archive size={15} />Archivieren</>}
                    </MenueEintrag>
                    <MenueEintrag gefahr onClick={() => { schliessen(); onLoeschen(z); }}><Trash size={15} />Löschen</MenueEintrag>
                  </>
                )}
              </Popover>
            </div>
          </div>
        ))}
        <div className="flex justify-between" style={{ padding: '12px 20px', fontSize: 12, color: 'var(--ink3)' }}>
          <span>{zeilen.length} {zeilen.length === 1 ? 'Objekt' : 'Objekte'}</span>
          <span className="immo-zahl">{einheiten.toLocaleString('de-DE')} Einheiten · {qm(Math.round(flaeche * 100) / 100)}</span>
        </div>
      </div>
    </section>
  );
}
